import { useEffect, useState, useCallback } from 'react';
import {
  adCatalogApi,
  type AnalyticsQueryMeta,
  type AnalyticsQueryResult,
  type AttributionCoverage,
} from '@/services/api';
import {
  BarChart3,
  Loader2,
  Play,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  TrendingUp,
  Users,
  Zap,
  Target,
  Clock,
  Layers,
  Filter as FilterIcon,
  Sparkles,
  ArrowUpDown,
  GitBranch,
  Shield,
} from 'lucide-react';
import { clsx } from 'clsx';

const CATEGORY_META: Record<string, { label: string; icon: typeof BarChart3; color: string }> = {
  opportunity: { label: 'Opportunity', icon: Zap, color: 'text-amber-600 bg-amber-50' },
  timing: { label: 'Timing', icon: Clock, color: 'text-blue-600 bg-blue-50' },
  efficiency: { label: 'Efficiency', icon: Target, color: 'text-emerald-600 bg-emerald-50' },
  optimization: { label: 'Optimization', icon: TrendingUp, color: 'text-purple-600 bg-purple-50' },
  creative: { label: 'Creative', icon: Sparkles, color: 'text-pink-600 bg-pink-50' },
  format: { label: 'Format', icon: Layers, color: 'text-indigo-600 bg-indigo-50' },
  funnel: { label: 'Funnel', icon: FilterIcon, color: 'text-cyan-600 bg-cyan-50' },
  persona: { label: 'Persona', icon: Users, color: 'text-violet-600 bg-violet-50' },
  campaign: { label: 'Campaign', icon: BarChart3, color: 'text-slate-600 bg-slate-50' },
  strategy: { label: 'Strategy', icon: Target, color: 'text-red-600 bg-red-50' },
  diagnostic: { label: 'Diagnostic', icon: AlertCircle, color: 'text-orange-600 bg-orange-50' },
  attribution: { label: 'Attribution', icon: GitBranch, color: 'text-fuchsia-600 bg-fuchsia-50' },
};

function formatCell(value: unknown, column: string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    if (column.includes('pct') || column.includes('rate') || column.includes('ctr')) return `${value.toFixed(1)}%`;
    if (column.includes('spend') || column.includes('cac') || column.includes('cpa') || column.includes('ltv') || column.includes('cpm') || column.includes('cpc') || column.includes('budget')) return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toFixed(2);
  }
  return String(value);
}

function columnDisplayName(col: string): string {
  return col
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace('Pct', '%')
    .replace('Ctr', 'CTR')
    .replace('Cpm', 'CPM')
    .replace('Cpc', 'CPC')
    .replace('Cac', 'CAC')
    .replace('Ltv', 'LTV')
    .replace('Atc', 'ATC');
}

function ResultTable({ result }: { result: AnalyticsQueryResult }) {
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const handleSort = (col: string) => {
    if (sortCol === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(col);
      setSortAsc(false);
    }
  };

  let rows = [...result.rows];
  if (sortCol) {
    rows.sort((a, b) => {
      const av = a[sortCol] ?? '';
      const bv = b[sortCol] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortAsc ? av - bv : bv - av;
      return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }

  const actionCol = result.columns.find(c => c === 'budget_action' || c === 'signal_strength' || c === 'fatigue_status' || c === 'quadrant');

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {result.columns
              .filter(c => c !== 'ad_id')
              .map((col) => (
              <th
                key={col}
                onClick={() => handleSort(col)}
                className="px-3 py-2 text-left font-medium text-slate-600 cursor-pointer hover:bg-slate-100 whitespace-nowrap select-none"
              >
                <span className="inline-flex items-center gap-1">
                  {columnDisplayName(col)}
                  {sortCol === col && <ArrowUpDown className="w-3 h-3 text-brand-teal" />}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={clsx('border-b border-slate-100', i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30')}>
              {result.columns
                .filter(c => c !== 'ad_id')
                .map((col) => {
                const val = row[col];
                const isAction = col === actionCol;
                let actionColor = '';
                if (isAction && typeof val === 'string') {
                  if (val.startsWith('TEST') || val.startsWith('strong') || val.startsWith('Q2')) actionColor = 'text-emerald-700 bg-emerald-50';
                  else if (val.startsWith('SCALE') || val.startsWith('moderate') || val.startsWith('Q1')) actionColor = 'text-blue-700 bg-blue-50';
                  else if (val.startsWith('REFRESH') || val.startsWith('critical') || val.startsWith('warning') || val.startsWith('Q4')) actionColor = 'text-amber-700 bg-amber-50';
                  else if (val.startsWith('PAUSE') || val.startsWith('Q3')) actionColor = 'text-red-700 bg-red-50';
                  else actionColor = 'text-slate-600 bg-slate-50';
                }
                return (
                  <td key={col} className={clsx(
                    'px-3 py-2 whitespace-nowrap',
                    typeof val === 'number' && 'text-right tabular-nums',
                    col.includes('name') && 'max-w-[200px] truncate',
                    col.includes('theme') && 'max-w-[180px] truncate',
                    isAction && actionColor && 'font-medium',
                  )}>
                    {isAction && actionColor ? (
                      <span className={clsx('inline-block px-2 py-0.5 rounded text-[11px]', actionColor)}>
                        {String(val)}
                      </span>
                    ) : (
                      formatCell(val, col)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QueryCard({
  query,
  result,
  isRunning,
  isExpanded,
  onToggle,
  onRun,
}: {
  query: AnalyticsQueryMeta;
  result: AnalyticsQueryResult | null;
  isRunning: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onRun: () => void;
}) {
  const cat = CATEGORY_META[query.category] || CATEGORY_META.campaign;
  const Icon = cat.icon;

  return (
    <div className={clsx(
      'bg-white rounded-xl border transition-shadow',
      isExpanded ? 'border-brand-teal/30 shadow-md' : 'border-slate-200 hover:shadow-sm',
    )}>
      <div
        className="flex items-start gap-3 p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className={clsx('p-2 rounded-lg shrink-0', cat.color)}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-800 truncate">{query.title}</h3>
            <span className={clsx('text-[10px] px-1.5 py-0.5 rounded-full font-medium', cat.color)}>
              {cat.label}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 italic">"{query.question}"</p>
          {query.description && (
            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{query.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {result && (
            <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
              {result.row_count} rows
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onRun(); }}
            disabled={isRunning || !query.available}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              query.available
                ? 'bg-brand-teal text-white hover:bg-brand-teal/90 disabled:opacity-50'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed',
            )}
          >
            {isRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          </button>
          {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
      </div>

      {isExpanded && result && (
        <div className="px-4 pb-4 pt-0">
          {result.error ? (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {result.error}
            </div>
          ) : result.rows.length > 0 ? (
            <ResultTable result={result} />
          ) : (
            <div className="text-xs text-slate-400 italic py-4 text-center">No results</div>
          )}
        </div>
      )}
    </div>
  );
}

function AttributionCoverageCard({ coverage }: { coverage: AttributionCoverage | null }) {
  if (!coverage) return null;
  const stats = [
    { label: 'Ads w/ Attribution', value: `${coverage.ads_with_attribution} / ${coverage.total_ads}`, pct: coverage.ad_attribution_rate },
    { label: 'Identity Resolution', value: `${coverage.unique_resolved_customers.toLocaleString()} customers`, pct: coverage.identity_resolution_pct },
    { label: 'Ad-ID Coverage', value: `${coverage.total_funnel_events.toLocaleString()} events`, pct: coverage.events_with_ad_id_pct },
    { label: 'Campaign Attribution', value: `${coverage.total_persona_assignments} personas`, pct: coverage.campaign_attribution_pct },
  ];

  return (
    <div className="bg-gradient-to-r from-fuchsia-50 to-purple-50 border border-fuchsia-200/60 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-fuchsia-600" />
        <h3 className="text-sm font-semibold text-fuchsia-800">Attribution Bridge Coverage</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-white/70 rounded-lg p-2.5">
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{s.label}</div>
            <div className="text-sm font-bold text-slate-800 mt-0.5">{s.pct}%</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{s.value}</div>
            <div className="mt-1.5 h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-fuchsia-500 rounded-full transition-all"
                style={{ width: `${Math.min(s.pct, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdAnalyticsPage() {
  const [queries, setQueries] = useState<AnalyticsQueryMeta[]>([]);
  const [results, setResults] = useState<Record<number, AnalyticsQueryResult>>({});
  const [running, setRunning] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [coverage, setCoverage] = useState<AttributionCoverage | null>(null);

  useEffect(() => {
    adCatalogApi.listAnalyticsQueries()
      .then(setQueries)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    adCatalogApi.getAttributionCoverage()
      .then(setCoverage)
      .catch(() => {});
  }, []);

  const runQuery = useCallback(async (queryId: number) => {
    setRunning((prev) => new Set(prev).add(queryId));
    setExpanded((prev) => new Set(prev).add(queryId));
    try {
      const result = await adCatalogApi.runAnalyticsQuery(queryId);
      setResults((prev) => ({ ...prev, [queryId]: result }));
    } catch (e) {
      setResults((prev) => ({
        ...prev,
        [queryId]: {
          query_id: queryId,
          title: '',
          question: '',
          category: '',
          description: '',
          columns: [],
          rows: [],
          row_count: 0,
          error: e instanceof Error ? e.message : 'Query failed',
        },
      }));
    } finally {
      setRunning((prev) => { const n = new Set(prev); n.delete(queryId); return n; });
    }
  }, []);

  const runAll = useCallback(async () => {
    const available = queries.filter((q) => q.available);
    for (const q of available) {
      await runQuery(q.id);
    }
  }, [queries, runQuery]);

  const categories = [...new Set(queries.map((q) => q.category))];
  const filtered = categoryFilter
    ? queries.filter((q) => q.category === categoryFilter)
    : queries;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-brand-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Ad Performance Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">
            {queries.filter((q) => q.available).length} analysis queries available
            {Object.keys(results).length > 0 && ` · ${Object.keys(results).length} executed`}
          </p>
        </div>
        <button
          onClick={runAll}
          disabled={running.size > 0}
          className="px-4 py-2 bg-brand-teal text-white text-sm font-medium rounded-lg hover:bg-brand-teal/90 disabled:opacity-50 flex items-center gap-2"
        >
          {running.size > 0 ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Running ({running.size})...</>
          ) : (
            <><Play className="w-4 h-4" /> Run All Queries</>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700">{error}</div>
      )}

      <AttributionCoverageCard coverage={coverage} />

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setCategoryFilter('')}
          className={clsx(
            'text-xs px-3 py-1.5 rounded-full font-medium transition-colors',
            !categoryFilter ? 'bg-brand-teal text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
          )}
        >
          All ({queries.length})
        </button>
        {categories.map((cat) => {
          const meta = CATEGORY_META[cat] || CATEGORY_META.campaign;
          const count = queries.filter((q) => q.category === cat).length;
          return (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat === categoryFilter ? '' : cat)}
              className={clsx(
                'text-xs px-3 py-1.5 rounded-full font-medium transition-colors',
                categoryFilter === cat ? 'bg-brand-teal text-white' : clsx('hover:opacity-80', meta.color),
              )}
            >
              {meta.label} ({count})
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        {filtered.map((q) => (
          <QueryCard
            key={q.id}
            query={q}
            result={results[q.id] || null}
            isRunning={running.has(q.id)}
            isExpanded={expanded.has(q.id)}
            onToggle={() => setExpanded((prev) => {
              const n = new Set(prev);
              n.has(q.id) ? n.delete(q.id) : n.add(q.id);
              return n;
            })}
            onRun={() => runQuery(q.id)}
          />
        ))}
      </div>
    </div>
  );
}
